import React from 'react'
import Select from 'react-select'
import {
  Opt,
  SOURCE_OPTIONS,
  SourceCode,
  sortOptionsForSources,
} from '../../../constants/carouselOptions'
import { Badge } from '../../Badge'
import EditorSection from '../EditorSection'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import type { CarouselMetadata } from './CarouselEditor'
import CarouselFilterGroups, { groupsOf } from './CarouselFilterGroups'
import CarouselPinsEditor from './CarouselPinsEditor'
import CarouselShowMoreEditor from './CarouselShowMoreEditor'
import { descriptorsForSources } from './carouselFilterRegistry'

interface CardCarouselEditorProps {
  metadata: CarouselMetadata
  onChange: (metadata: CarouselMetadata) => void
  /** Present once the widget row exists; required to upload pin artwork. */
  widgetId?: string
  /** The carousel variant being authored — it decides the card crop. */
  widgetType: string
}

const CardCarouselEditor: React.FC<CardCarouselEditorProps> = ({
  metadata, onChange, widgetId, widgetType,
}) => {
  const sources = (metadata.sources as SourceCode[]) ?? []

  // Apply a patch and drop any keys set to undefined, so the emitted config
  // never carries empty/stale fields.
  const apply = (patch: Partial<CarouselMetadata>) => {
    const next: CarouselMetadata = { ...metadata, ...patch }
    for (const k of Object.keys(next)) {
      if (next[k] === undefined) delete next[k]
    }
    onChange(next)
  }

  const handleSourcesChange = (next: SourceCode[]) => {
    // Drop filters that no longer apply to the new source set, and reconcile
    // the sort to one that's valid for it. Every group is pruned, not just the
    // first — a group left holding only dropped keys becomes empty, which
    // widens rather than narrows, so those are removed outright.
    const allowed = new Set(descriptorsForSources(next).map(d => d.key))
    const prune = (g: Record<string, unknown>) =>
      Object.fromEntries(Object.entries(g).filter(([k]) => allowed.has(k)))
    const pruned = groupsOf(metadata).map(prune).filter(g => Object.keys(g).length > 0)

    const sortOpts = sortOptionsForSources(next)
    const sort = sortOpts.some(o => o.value === metadata.sort) ? metadata.sort : sortOpts[0]?.value
    apply({
      sources: next.length ? next : undefined,
      // undefined clears a legacy `filters` on the next save; groups are the
      // only shape this editor writes.
      filters: undefined,
      filter_groups: pruned.length ? pruned : undefined,
      sort,
      ended_grace_minutes: next.includes('matches') ? metadata.ended_grace_minutes : undefined,
      // live_first is matches-only; ACE rejects it on any other source.
      live_first: next.includes('matches') ? metadata.live_first : undefined,
    })
  }

  const pinCount = (metadata.pins ?? []).length

  return (
    <>
      <EditorSection title="Content">
        <div className={formStyles.fieldGroup}>
          <label>Sources</label>
          <Select<Opt, true>
            isMulti
            options={SOURCE_OPTIONS}
            value={SOURCE_OPTIONS.filter(o => sources.includes(o.value as SourceCode))}
            onChange={(v) => handleSourcesChange(v.map(o => o.value as SourceCode))}
            className={selectStyles.reactSelect}
            classNamePrefix="react-select"
            placeholder="Leave empty for a fully curated rail…"
          />
          {sources.length === 0 && (
            <p className={formStyles.hint}>
              With no source the pinned cards below are the whole rail.
            </p>
          )}
        </div>

        {sources.length > 0 && (
          <CarouselFilterGroups sources={sources} metadata={metadata} onChange={onChange} />
        )}
      </EditorSection>

      <EditorSection
        title="Pinned cards"
        badge={pinCount > 0 ? <Badge variant="secondary" size="small">{pinCount}</Badge> : undefined}
        defaultCollapsed={pinCount === 0}
      >
        <CarouselPinsEditor
          metadata={metadata}
          onChange={onChange}
          widgetId={widgetId}
          widgetType={widgetType}
        />
      </EditorSection>

      {sources.length > 0 && (
        <EditorSection title="See more link" defaultCollapsed={!metadata.show_more}>
          <CarouselShowMoreEditor metadata={metadata} onChange={onChange} />
        </EditorSection>
      )}
    </>
  )
}

export default CardCarouselEditor
